import { HttpClient } from "@angular/common/http";
import { computed, effect, inject, Injectable, signal } from "@angular/core";
import { ToolConfiguration } from "@aws-sdk/client-bedrock-runtime";
import { I18n } from "@corporate/ng-tebis-base-components";
import { AuthService } from "../../01_core/auth/auth.service";
import { AIChatMessage, AIComponentMessage, AIStandardMessage, UploadedFileContext } from "../../03_model/ai-chat-message.model";
import { AITool } from "../../03_model/ai-tool.interface";
import { CreateOrderTool } from "../../06_component/manufacturing-order/tools/create-order.tool";
import { CreateTaskTool } from "../../06_component/task/tools/create-task.tool";
import { firstValueFrom } from "rxjs";
import { environment } from "../../../environments/environment";
import { SearchCustomerTool } from "../../06_component/customer/tools/search-customer.tool";
import { SearchOrderTool } from "../../06_component/manufacturing-order/tools/search-order";
import { AssistantSuggestionsTool } from "../../06_component/assistant/assistant-suggestions/assistant-suggestions.tool";
import { DisplayOrdersTool } from "../../06_component/manufacturing-order/tools/display-orders";
import { AssistantSuggestionsComponent } from "../../06_component/assistant/assistant-suggestions/assistant-suggestions.component";
import { extractSuggestionsFromAssistantMessage } from "./extract-suggestions";
import { CreateTaskMessageComponent } from "../../06_component/task/messages/create-task-message/create-task-message.component";
import { TaskCreatedMessageComponent } from "../../06_component/task/messages/task-created-message/task-created-message.component";
import { CreateOrderCompactComponent } from "../../06_component/manufacturing-order/create-order-compact/create-order-compact.component";
import { OrderCreatedMessageComponent } from "../../06_component/manufacturing-order/messages/order-created-message/order-created-message.component";
import { OrderSearchResultMessageComponent, OrderSearchResultMessageData } from "../../06_component/manufacturing-order/messages/order-search-result-message/order-search-result-message.component";

@Injectable({ providedIn: 'root' })
export class AssistantService {

  private readonly maxFileChars = 12000;
  private readonly maxFilesInContext = 8;

  http = inject(HttpClient);
  authService = inject(AuthService);
  authorizedHttp = this.authService.authorize(this.http);
  i18n = inject(I18n);
  uploadedFiles = signal<UploadedFileContext[]>([]);

  systemPrompt = computed(() => `You are the assistant of the Tebis Process Solution (Tebis Prozesslösung), a manufacturing execution system for small and medium enterprises, developed by the german CAD/CAM software company Tebis.
Your job is to help users with requests.

The database has these tables:
customer
order
article

Each table stores items of a specific type.

For each given tool, evaluate if it is useful for the current request.
Evaluate if you can process the user's request with the given tools.
If you can't answer a question, or you are not responsible for the topic, such as "What's for lunch?" or "What is the meaning of life?", say that you don't know, instead of trying to use the tools in a wrong way or giving incorrect information.
Always keep your answers as short as possible.
If you need more information, ask the user directly for it, but don't ask for specific formats like ISO 8601.

Break tasks down and think step by step, even if the given task seems simple at first.
If it really turns out to be simple, great! Rather think step by step than trying to skip steps. This way you won't miss important details and you will be more likely to come up with the right answer.

Do not use markdown or code blocks in your answers, just plain text.
The user selected the language ${this.i18n.lang()}. Use this language in your answers and questions.
Address the user politely but informally. For example, in German say "Du" instead of "Sie". In other languages, use the equivalent informal form of address.
`);

  demoMessages = <AIChatMessage[]>[
    <AIComponentMessage>{ type: 'component', role: 'assistant', component: CreateTaskMessageComponent, contentForLLM: 'The assistant can create tasks for the user. Displaying a form to create a task.', data: { title: '', description: '', }, requiresFollowUp: false, },
    <AIComponentMessage>{ type: 'component', role: 'assistant', component: TaskCreatedMessageComponent, contentForLLM: 'The assistant can create tasks for the user. Displaying a message that a task was created.', data: { taskID: 'task-1', sentDate: new Date(), }, requiresFollowUp: false, },
    <AIComponentMessage>{ type: 'component', role: 'assistant', component: AssistantSuggestionsComponent, contentForLLM: 'The assistant can provide suggestions to the user. Displaying a list of suggestions.', data: { suggestions: [ 'create a new order' ], messageText: 'What would you like to do next?' }, requiresFollowUp: false, },
    <AIComponentMessage>{ type: 'component', role: 'assistant', component: CreateOrderCompactComponent, contentForLLM: 'The assistant can create orders for the user. Displaying a form to create an order.', data: { title: '', description: '', }, requiresFollowUp: false, },
    <AIComponentMessage>{ type: 'component', role: 'assistant', component: OrderCreatedMessageComponent, contentForLLM: 'Displaying a message that an order was created.', data: { title: '', description: '', }, requiresFollowUp: false, },
    <AIComponentMessage>{ type: 'component', role: 'assistant', component: OrderSearchResultMessageComponent, contentForLLM: 'Displaying order search results.', data: <OrderSearchResultMessageData>{ message: 'I found these orders:', orderIDs: [] }, requiresFollowUp: false, },
  ];

  messages = signal<AIChatMessage[]>([
    <AIStandardMessage>{ type: 'standard', role: 'assistant', content: '', requiresFollowUp: false },
    // ...this.demoMessages,
  ]);

  tools = <AITool[]>[
    new SearchCustomerTool(),
    new SearchOrderTool(),
    new CreateOrderTool(),
    new DisplayOrdersTool(),
    new CreateTaskTool(),
    new AssistantSuggestionsTool(),
  ];

  toolMap: Record<string, AITool> = this.tools.reduce((map, tool) => {
    map[tool.tool_name] = tool;
    return map;
  }, {} as Record<string, AITool>);

  toolConfig = <ToolConfiguration>{
    tools: this.tools.map(tool => tool.description),
  }

  constructor() {
    this.i18n.addNamespace('assistant');

    effect(() => {
      const message = this.i18n.translate('assistantOpeningMessage')();
      if (message !== 'assistantOpeningMessage') {
        this.messages.update(messages => {
          messages[0] = <AIStandardMessage>{ type: 'standard', role: 'assistant', content: message, requiresFollowUp: false };
          return [...messages];
        });
      }
    });
  }

  private buildFilesContextPrompt(): string {
    const contexts = this.uploadedFiles();
    if (!contexts.length) {
      return '';
    }

    const fileDescriptions = contexts.map((file, index) => {
      const excerpt = file.contentExcerpt || '[No readable text content]';
      return `File ${index + 1}: ${file.name}\nType: ${file.type || 'unknown'}\nSize: ${file.size} bytes\nTruncated: ${file.isTruncated ? 'yes' : 'no'}\nContent:\n${excerpt}`;
    }).join('\n\n---\n\n');

    return `Uploaded file context (from the browser upload/drag-drop):\n${fileDescriptions}\n\nWhen files are present, evaluate what useful actions can be taken with them in the Tebis Process Solution and offer short, concrete suggestions to the user. If information is missing, ask concise follow-up questions.`;
  }

  private async readFileContext(file: File): Promise<UploadedFileContext> {
    try {
      const rawText = await file.text();
      const normalizedText = rawText.replace(/\0/g, '').trim();
      const isTruncated = normalizedText.length > this.maxFileChars;
      const contentExcerpt = normalizedText.slice(0, this.maxFileChars);

      return {
        name: file.name,
        type: file.type,
        size: file.size,
        contentExcerpt,
        isTruncated,
      };
    } catch {
      return {
        name: file.name,
        type: file.type,
        size: file.size,
        contentExcerpt: '[File could not be read as text in the browser]',
        isTruncated: false,
      };
    }
  }

  async addFilesAsContext(files: FileList | File[]) {
    const filesToRead = Array.from(files).filter(file => file.size > 0);
    if (!filesToRead.length) {
      return;
    }

    const fileContexts = await Promise.all(filesToRead.map(file => this.readFileContext(file)));
    const updatedContexts = [...this.uploadedFiles(), ...fileContexts].slice(-this.maxFilesInContext);
    this.uploadedFiles.set(updatedContexts);

    const uploadedNames = fileContexts.map(file => file.name).join(', ');
    this.messages.set([
      ...this.messages(),
      {
        type: 'standard',
        role: 'user',
        content: `${this.i18n.translate('assistant:addedFileContext')()}: ${uploadedNames}`,
        requiresFollowUp: false,
      },
    ]);

    const instructions = `The user uploaded files. Analyze the uploaded file context and infer what can be done with the information or what the user might want to do next. Offer up to 3 concrete short suggestions, using around 5 words each. Use the display_suggestions tool. In the messageText, write a short description what kind of files the user provided. It looks most natural if you say something like: "You provided an email that requests an offer for a product".`;
    await this.callLLM(instructions);
  }

  removeUploadedFileContext(index: number) {
    const files = this.uploadedFiles();
    if (index < 0 || index >= files.length) {
      return;
    }

    const removedFile = files[index];
    this.uploadedFiles.set(files.filter((_, i) => i !== index));
    this.messages.set([
      ...this.messages(),
      {
        type: 'standard',
        role: 'user',
        content: `${this.i18n.translate('assistant:removedFileContext')()}: ${removedFile.name}`,
        requiresFollowUp: false,
      },
    ]);
  }

  async addMessage(message: AIChatMessage) {
    this.messages.set([...this.messages(), message]);

    if (message.requiresFollowUp) {
      await this.callLLM();
    }
  }

  async callLLM(instructions?: string) {
    const messages = this.messages().map((msg, i) => {
      switch (msg.type) {
        case 'component': return {
          role: msg.role == 'user' ? 'user' : 'assistant',
          content: [{ text: msg.contentForLLM }],
        };
        case 'standard': return {
          role: msg.role == 'assistant' ? 'assistant' : 'user',
          content: [{ text: msg.content! }],
        };
        case 'tool-response': return {
          role: 'user',
          content: [{ text: msg.content! }],
        };
        case 'tool-call': {
          return {
            role: 'assistant',
            content: [{ text: msg.content! }],
          };
        }
      }
      return null;
    }).filter(m => m != null) as any[];

    const systemMessages = [{ text: this.systemPrompt() }];
    const filesContextPrompt = this.buildFilesContextPrompt();
    if (filesContextPrompt) {
      systemMessages.push({ text: filesContextPrompt });
    }
    if (instructions) {
      systemMessages.push({ text: instructions });
    }

    const requestBody = {
      messages: messages,
      system: systemMessages,
      toolConfig: this.toolConfig,
    };

    const response = await firstValueFrom(
      this.authorizedHttp.post<any>(`${environment.baseUrl}/bedrock/gpt-oss-20b`, requestBody)
    );

    if (response.stopReason === 'tool_use') {
      const content = response.output?.message?.content || [];
      let followUp = false;
      for (const c of content) {
        const toolName = c.toolUse?.name;
        const toolInput = c.toolUse?.input;
        if (toolName && toolInput && toolInput instanceof Object) {
          const tool = this.toolMap[toolName];
          if (tool) {
            const toolResponse = await tool.invoke(toolInput);
            this.messages.set([...this.messages(), { type: 'tool-call', role: 'assistant', content: `Called tool ${toolName} with input ${JSON.stringify(toolInput)}`, requiresFollowUp: false }, toolResponse]);
            if (toolResponse.requiresFollowUp) {
              followUp = true;
            }
          }
        }
      }
      if (followUp) {
        await this.callLLM();
      }
      return;
    }

    const content = response.output?.message?.content || [];
    // const aiMessage = content.map(c => c.reasoningContent?.reasoningText?.text || c.text).join('\n');
    const aiMessage = content.filter((c: any) => c.text).map((c: any) => c.text).join('\n');

    const extraction = await extractSuggestionsFromAssistantMessage(aiMessage, this.authorizedHttp);
    if (extraction.suggestions.length > 0) {
      this.messages.set([
        ...this.messages(),
        {
          type: 'component',
          role: 'assistant',
          component: AssistantSuggestionsComponent,
          contentForLLM: `Assistant message: ${extraction.messageText || '[empty]'}. Displaying suggestions: ${extraction.suggestions.join(', ')}`,
          data: {
            suggestions: extraction.suggestions,
            messageText: extraction.messageText,
          },
          requiresFollowUp: false,
        },
      ]);
      return;
    }

    this.messages.set([...this.messages(), { type: 'standard', role: 'assistant', content: aiMessage, requiresFollowUp: false }]);
  }

  setFormSubmitted(message: AIChatMessage) {
    const msgs = this.messages();
    const msgIndex = msgs.indexOf(message);
    if (msgIndex !== -1) {
      const updatedMessage = { ...msgs[msgIndex], formSubmitted: true };
      const newMessages = [...msgs];
      newMessages[msgIndex] = updatedMessage;
      this.messages.set(newMessages);
    }
  }

  updateMessage(message: AIChatMessage, newData: object) {
    const msgs = this.messages();
    const msgIndex = msgs.indexOf(message);
    if (message.type == 'component' && msgIndex !== -1) {
      const updatedMessage = { ...msgs[msgIndex], data: { ...(msgs[msgIndex] as AIComponentMessage).data, ...newData } };
      const newMessages = [...msgs];
      newMessages[msgIndex] = updatedMessage;
      this.messages.set(newMessages);
    }
  }
}
